import React, { useState, useMemo, useCallback } from "react";
import rentalQuestions from "../data/rental_questions.json";
import SectionTitle from "../components/SectionTitle";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import rentalForm from "../assets/lottie-files/rentalForm.lottie";
import marketMap from "../assets/marketMap.png";
import styled, { css, keyframes } from "styled-components";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

// Styled Components
const Container = styled.div`
  width: 90%;
  max-width: 100%;
  margin: 3rem auto;
  padding: 3.5rem 3rem;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    padding: 2.5rem 1.2rem;
    width: 95%;
  }
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    width: 98%;
    margin: 1rem auto;
  }
  @media (max-width: 600px) {
    padding: 1.5rem 0.8rem;
    border-radius: 16px;
    margin: 0.5rem auto;
    width: 100%;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  }
  @media (max-width: 480px) {
    padding: 1rem 0.5rem;
    margin: 0;
    border-radius: 12px;
  }
`;

const StyledSectionTitle = styled.h2`
  font-size: 1.65rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2.5rem;
  padding-bottom: 0.85rem;
  margin-top: 0;
  z-index: 2;
  position: sticky;
  top: 0;
  left: 0;
  padding-top: 1.5rem;
  letter-spacing: -0.03em;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, rgb(222, 110, 73), rgb(255, 140, 100));
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 2rem;
    padding-bottom: 0.7rem;
  }
  @media (max-width: 600px) {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
    padding-bottom: 0.6rem;
  }
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1.2rem;
    padding-bottom: 0.5rem;
  }
`;

const FormLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
`;

const StepContentScroll = styled.div`
  padding-bottom: 2.5rem;
  position: relative;
  z-index: 1;
`;

// Animation for section header
const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedSectionTitle = styled(StyledSectionTitle)<{ animate: boolean }>`
  ${({ animate }) =>
    animate &&
    css`
      animation: ${fadeSlideIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    `}
`;

const FormField = styled.div`
  margin-bottom: 2rem;
  animation: fadeInUp 0.5s ease;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.7rem;
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1.1rem;
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 0.6rem;
  }
  @media (max-width: 600px) {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.4rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  background-color: #ffffff;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  font-family: inherit;

  &::placeholder {
    color: #adb5bd;
  }

  &:hover {
    border-color: #ced4da;
  }

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.1);
    background-color: #ffffff;
  }

  /* For date and time inputs, make the entire field clickable */
  &[type="date"],
  &[type="time"] {
    cursor: pointer;
    position: relative;

    /* Make the calendar/clock icon cover the entire input */
    &::-webkit-calendar-picker-indicator {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    /* Add a custom icon on the right side */
    &::after {
      content: "";
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
    }
  }

  /* Custom calendar icon for date inputs */
  &[type="date"] {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23666' viewBox='0 0 16 16'%3E%3Cpath d='M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 16px;
  }

  /* Custom clock icon for time inputs */
  &[type="time"] {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23666' viewBox='0 0 16 16'%3E%3Cpath d='M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z'/%3E%3Cpath d='M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 16px;
  }

  /* For number inputs */
  &[type="number"] {
    -moz-appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    border-radius: 12px;
  }
  @media (max-width: 600px) {
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
    border-radius: 10px;
  }
  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    border-radius: 8px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 2.5rem;
`;

const DropdownHeader = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  background-color: #ffffff;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  user-select: none;

  &:hover {
    border-color: #ced4da;
  }

  &:active {
    transform: scale(0.99);
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      border-color: rgb(222, 110, 73);
      box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.1);
    `}

  span {
    transition: transform 0.2s ease;
    display: inline-block;
  }
`;

const DropdownList = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  padding: 0.5rem 0;
  margin: 4px 0 0 0;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  max-height: ${({ isOpen }) => (isOpen ? "200px" : "0")};
  overflow-y: auto;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  position: absolute;
  width: 100%;
  z-index: 5;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e9ecef;
    border-radius: 8px;
  }
`;

const DropdownOption = styled.li`
  padding: 0.7rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease;
  user-select: none;

  &:hover {
    background: rgba(222, 110, 73, 0.08);
  }

  &:active {
    background: rgba(222, 110, 73, 0.15);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.85rem 1.2rem;
  font-size: 1rem;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  background-color: #ffffff;
  resize: vertical;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  line-height: 1.6;
  font-family: inherit;

  &::placeholder {
    color: #adb5bd;
  }

  &:hover {
    border-color: #ced4da;
  }

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.1);
    background-color: #ffffff;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.95rem;
    min-height: 100px;
    border-radius: 12px;
  }
  @media (max-width: 600px) {
    padding: 0.7rem 0.9rem;
    font-size: 0.9rem;
    min-height: 90px;
    border-radius: 10px;
  }
  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
    min-height: 80px;
    border-radius: 8px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.2rem;
  padding: 1.8rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);

  &:hover {
    border-color: #ced4da;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 14px;
  }
  @media (max-width: 600px) {
    gap: 0.8rem;
    padding: 1.2rem;
    border-radius: 12px;
  }
  @media (max-width: 480px) {
    gap: 0.6rem;
    padding: 1rem;
    border-radius: 10px;
    flex-direction: column;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2a3a8c;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.7rem 1rem;
  background: white;
  border-radius: 10px;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  border: 2px solid transparent;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  user-select: none;

  &:hover {
    background: #f8f9fa;
    box-shadow: 0 3px 8px rgba(222, 110, 73, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }

  input {
    margin-right: 0.85rem;
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: rgb(222, 110, 73);
    flex-shrink: 0;
  }

  &:has(input:checked) {
    border-color: rgb(222, 110, 73);
    background: linear-gradient(
      135deg,
      rgba(222, 110, 73, 0.08) 0%,
      #ffffff 100%
    );
    box-shadow: 0 2px 8px rgba(222, 110, 73, 0.12);
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.6rem 0.9rem;
    border-radius: 8px;
  }
  @media (max-width: 600px) {
    font-size: 0.9rem;
    padding: 0.5rem 0.8rem;
    border-radius: 6px;
  }
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.4rem 0.7rem;
    border-radius: 5px;
    width: 100%;
    justify-content: flex-start;
  }
`;

const Button = styled.button<{ secondary?: boolean }>`
  background: ${({ secondary }) =>
    secondary
      ? "linear-gradient(135deg, #dee2e6, #f1f3f5)"
      : "linear-gradient(135deg, rgb(222, 110, 73), rgb(200, 90, 60))"};
  color: ${({ secondary }) => (secondary ? "#1a1a1a" : "white")};
  padding: 0.75rem 1.8rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  margin-right: 1rem;
  box-shadow: 0 6px 15px rgba(222, 110, 73, 0.18);
  transition:
    background 0.4s ease,
    box-shadow 0.4s ease,
    transform 0.2s ease,
    color 0.2s;

  &:hover {
    background: ${({ secondary }) =>
      secondary
        ? "linear-gradient(135deg, #f1f3f5, #dee2e6)"
        : "linear-gradient(135deg, rgb(200, 90, 60), rgb(222, 110, 73))"};
    box-shadow: 0 8px 20px rgba(222, 110, 73, 0.12);
    color: ${({ secondary }) => (secondary ? "#1a1a1a" : "#fff")};
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.18);
  }

  @media (max-width: 768px) {
    padding: 0.7rem 1.5rem;
    font-size: 1.05rem;
    border-radius: 12px;
    margin-right: 0.8rem;
  }
  @media (max-width: 600px) {
    padding: 0.6rem 1.2rem;
    font-size: 1rem;
    border-radius: 10px;
    margin-right: 0.6rem;
  }
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
    font-size: 0.95rem;
    border-radius: 8px;
    margin-right: 0.5rem;
    width: 100%;
    margin-bottom: 0.5rem;
  }
`;

const FieldNote = styled.p`
  font-size: 0.9rem;
  color: rgb(0, 0, 0);
  margin-top: 0.4rem;
  margin-bottom: 3;
  line-height: 1.4;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-left: 3px solid rgb(222, 110, 73);
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
`;

const MarketMapImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 1.5rem 0;
  border: 2px solid rgba(222, 110, 73, 0.2);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 6px 20px rgba(222, 110, 73, 0.15);
  }
`;

const ImageModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  cursor: default;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(222, 110, 73, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(222, 110, 73, 1);
  }
`;

const SummaryActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid rgba(222, 110, 73, 0.1);

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
  }
  @media (max-width: 600px) {
    margin-top: 1.2rem;
    padding-top: 1.2rem;
    gap: 0.8rem;
  }
  @media (max-width: 480px) {
    margin-top: 1rem;
    padding-top: 1rem;
    gap: 0.5rem;
    flex-direction: column;
  }
`;

const popIn = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
`;

const ThankYouContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  animation: ${popIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 600px) {
    padding: 2rem 1rem;
  }
`;

const ThankYouIcon = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgb(222, 110, 73), rgb(255, 140, 100));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 24px rgba(222, 110, 73, 0.3);

  svg {
    width: 48px;
    height: 48px;
    stroke: white;
    stroke-width: 2.5;
    fill: none;
  }
`;

const ThankYouTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 1.4rem;
  }
`;

const ThankYouMessage = styled.p`
  font-size: 1.1rem;
  color: #6c757d;
  margin: 0 0 2.5rem 0;
  max-width: 500px;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const RatingSection = styled.div`
  width: 100%;
  max-width: 480px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 2rem;
  animation: ${fadeSlideIn} 0.5s 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;

  @media (max-width: 600px) {
    padding: 1.5rem;
  }
`;

const RatingLabel = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.8rem 0;
`;

const StarsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.2rem;
`;

const Star = styled.button<{ filled: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem;
  transition: transform 0.15s ease;

  svg {
    width: 36px;
    height: 36px;
    fill: ${({ filled }) => (filled ? "#f5a623" : "#dee2e6")};
    stroke: ${({ filled }) => (filled ? "#e8961c" : "#ced4da")};
    stroke-width: 1;
    transition: fill 0.15s ease, stroke 0.15s ease;
  }

  &:hover {
    transform: scale(1.2);

    svg {
      fill: #f5a623;
      stroke: #e8961c;
    }
  }

  &:active {
    transform: scale(0.95);
  }
`;

const RatingTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  background-color: #ffffff;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  margin-bottom: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #adb5bd;
  }

  &:focus {
    outline: none;
    border-color: rgb(222, 110, 73);
    box-shadow: 0 0 0 3px rgba(222, 110, 73, 0.1);
  }
`;

const RatingSubmitButton = styled.button`
  background: linear-gradient(135deg, rgb(222, 110, 73), rgb(200, 90, 60));
  color: white;
  padding: 0.6rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(222, 110, 73, 0.18);
  transition: background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, rgb(200, 90, 60), rgb(222, 110, 73));
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(222, 110, 73, 0.25);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const RatingThankYou = styled.p`
  font-size: 1rem;
  color: rgb(222, 110, 73);
  font-weight: 600;
  margin: 0.5rem 0 0 0;
  animation: ${fadeSlideIn} 0.4s ease;
`;

const ErrorBanner = styled.div`
  background: #fff5f5;
  border: 2px solid #fecaca;
  border-radius: 12px;
  padding: 0.8rem 1.2rem;
  margin-bottom: 1.5rem;
  color: #dc2626;
  font-weight: 500;
  font-size: 0.95rem;
  text-align: center;
  animation: ${fadeSlideIn} 0.3s ease;
`;

const HeroSection = styled.div`
  background: linear-gradient(
    to bottom right,
    rgb(222, 110, 73) 65%,
    rgb(240, 240, 240) 100%
  );
  padding: 4rem 1rem;
  color: white;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
  @media (max-width: 600px) {
    padding: 2.5rem 0.8rem;
  }
  @media (max-width: 480px) {
    padding: 2rem 0.5rem;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1.5rem;
  }
  @media (max-width: 600px) {
    gap: 1rem;
  }
  @media (max-width: 480px) {
    gap: 0.8rem;
  }
`;

const HeroText = styled.div`
  flex: 1 1 500px;
  min-width: 300px;

  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: auto;
    width: 100%;
  }
`;

const HeroAnimation = styled.div`
  flex: 1 1 400px;
  min-width: 300px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: auto;
    width: 100%;
    max-width: 400px;
  }
  @media (max-width: 600px) {
    max-width: 350px;
  }
  @media (max-width: 480px) {
    max-width: 300px;
  }
`;

// Day Card Components for Multi-Day Customization
const DayCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: 2rem 0;
`;

const DayCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(222, 110, 73, 0.3);
    box-shadow: 0 6px 16px rgba(222, 110, 73, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }
  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const DayCardHeader = styled.div<{ isClickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(222, 110, 73, 0.1);
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
  transition: background-color 0.2s ease;
  border-radius: 8px;
  padding: 1rem;
  margin: -1rem -1rem 1.5rem -1rem;

  ${({ isClickable }) =>
    isClickable &&
    `
    &:hover {
      background-color: rgba(222, 110, 73, 0.03);
    }
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const DayCardTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;

  span {
    font-size: 1.4rem;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const ExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  transform: ${({ isExpanded }) =>
    isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
  color: rgb(222, 110, 73);
  margin-left: 0.5rem;
`;

const DayCardContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${({ isExpanded }) => (isExpanded ? "5000px" : "0")};
  overflow: hidden;
  transition: max-height 0.4s ease-in-out;
`;

const CopyButton = styled.button`
  background: linear-gradient(135deg, #e9ecef, #f8f9fa);
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: linear-gradient(135deg, #dee2e6, #e9ecef);
    border-color: rgb(222, 110, 73);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

const DayCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const DayCardField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Date Multi-Select Calendar Components
const DatePickerContainer = styled.div`
  display: block;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e9ecef;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin: 1rem 0;
  max-width: 100%;
  width: fit-content;

  @media (max-width: 768px) {
    padding: 1.2rem;
    width: 100%;
  }
  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;

  @media (max-width: 600px) {
    gap: 0.3rem;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(222, 110, 73, 0.1);
`;

const CalendarNavButton = styled.button`
  background: linear-gradient(135deg, #e9ecef, #f8f9fa);
  color: #495057;
  border: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #dee2e6, #e9ecef);
    border-color: rgb(222, 110, 73);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

const CalendarMonth = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const DayOfWeekLabel = styled.div`
  text-align: center;
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
  padding: 0.5rem 0;

  @media (max-width: 600px) {
    font-size: 0.75rem;
    padding: 0.3rem 0;
  }
`;

const CalendarDay = styled.button<{
  isSelected: boolean;
  isDisabled: boolean;
  isToday: boolean;
}>`
  aspect-ratio: 1;
  border: 2px solid
    ${({ isSelected }) => (isSelected ? "rgb(222, 110, 73)" : "#e9ecef")};
  background: ${({ isSelected, isDisabled }) =>
    isDisabled
      ? "#f8f9fa"
      : isSelected
        ? "linear-gradient(135deg, rgba(222, 110, 73, 0.15), rgba(255, 140, 100, 0.1))"
        : "#ffffff"};
  color: ${({ isDisabled, isSelected }) =>
    isDisabled ? "#adb5bd" : isSelected ? "rgb(222, 110, 73)" : "#1a1a1a"};
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: ${({ isSelected, isToday }) =>
    isSelected || isToday ? "700" : "500"};
  cursor: ${({ isDisabled }) => (isDisabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  position: relative;

  ${({ isToday, isSelected }) =>
    isToday &&
    !isSelected &&
    `
    border-color: rgb(200, 90, 60);
    background: rgba(222, 110, 73, 0.05);
  `}

  &:hover:not(:disabled) {
    background: ${({ isSelected }) =>
      isSelected
        ? "linear-gradient(135deg, rgba(222, 110, 73, 0.25), rgba(255, 140, 100, 0.2))"
        : "rgba(222, 110, 73, 0.08)"};
    border-color: rgb(222, 110, 73);
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(222, 110, 73, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${({ isSelected }) =>
    isSelected &&
    `
    &::after {
      content: '✓';
      position: absolute;
      top: 2px;
      right: 4px;
      font-size: 0.7rem;
      color: rgb(222, 110, 73);
    }
  `}

  @media (max-width: 600px) {
    font-size: 0.75rem;
    border-radius: 6px;
  }
`;

const SelectedDatesChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem;
  border: 2px solid rgba(222, 110, 73, 0.15);
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(222, 110, 73, 0.03),
    rgba(255, 140, 100, 0.02)
  );
  width: 450px;
  max-width: 100%;
  max-height: 180px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(222, 110, 73, 0.05);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgb(222, 110, 73), rgb(255, 140, 100));
    border-radius: 10px;
    transition: background 0.3s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgb(200, 90, 60), rgb(222, 110, 73));
  }

  /* Firefox scrollbar styling */
  scrollbar-width: thin;
  scrollbar-color: rgb(222, 110, 73) rgba(222, 110, 73, 0.05);

  @media (max-width: 768px) {
    width: 100%;
    max-height: 150px;
  }

  @media (max-width: 600px) {
    max-height: 130px;
    padding: 0.8rem;
  }
`;

const DateChip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(
    135deg,
    rgba(222, 110, 73, 0.1),
    rgba(255, 140, 100, 0.08)
  );
  border: 1px solid rgba(222, 110, 73, 0.3);
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a1a;
  animation: chipFadeIn 0.3s ease-out;
  transition: all 0.2s ease;
  flex-shrink: 0;

  @keyframes chipFadeIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-5px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  &:hover {
    border-color: rgba(222, 110, 73, 0.5);
    box-shadow: 0 2px 8px rgba(222, 110, 73, 0.15);
    transform: translateY(-1px);
  }

  button {
    background: none;
    border: none;
    color: rgb(222, 110, 73);
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    padding: 0;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
    line-height: 1;

    &:hover {
      background: rgba(222, 110, 73, 0.2);
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.3rem 0.6rem;
  }
`;

const EmptyDatesPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.9rem;
  font-style: italic;
  min-height: 40px;
  text-align: center;

  @media (max-width: 600px) {
    font-size: 0.85rem;
    min-height: 35px;
  }
`;

// Error message display component
const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &::before {
    content: "⚠";
    font-size: 1rem;
  }

  @media (max-width: 600px) {
    font-size: 0.8rem;
  }
`;

// --- Dynamic Form State based on rental_questions.json ---
// We'll infer the form shape from the JSON, including support for multiselect and conditional fields.
type FormData = {
  [key: string]: any;
};

// Build initialFormData from rentalQuestions
function buildInitialFormData(questions: any[]): FormData {
  const data: FormData = {};
  for (const q of questions) {
    if (q.type === "checkbox") {
      data[q.id] = false;
    } else if (q.type === "multiselect") {
      data[q.id] = [];
      // For "other" option, support a details field
      if (q.options && q.options.some((opt: any) => opt.value === "Other")) {
        data[`${q.id}OtherDetails`] = "";
      }
    } else if (q.type === "number") {
      data[q.id] = q.default !== undefined ? q.default : "";
    } else {
      data[q.id] = "";
    }
    // Support for conditional fields (e.g., kitchenHours as a pair)
    if (q.conditional && q.conditional.addFields) {
      for (const addField of q.conditional.addFields) {
        data[addField.id] = "";
      }
    }
  }
  // Special handling for anticipatedAttendance - add the number field
  data["anticipatedAttendanceNumber"] = "";

  // Add fields for multi-day events
  if (!("eventStartDate" in data)) {
    data["eventStartDate"] = "";
  }
  if (!("eventEndDate" in data)) {
    data["eventEndDate"] = "";
  }
  if (!("multiDayConfiguration" in data)) {
    data["multiDayConfiguration"] = "";
  }
  if (!("selectedDates" in data)) {
    data["selectedDates"] = [];
  }
  if (!("setupAccessTime" in data)) {
    data["setupAccessTime"] = "";
  }

  // Add fields for recurring events
  if (!("recurringPattern" in data)) {
    data["recurringPattern"] = "";
  }
  if (!("recurringStartDate" in data)) {
    data["recurringStartDate"] = "";
  }
  if (!("recurringEndDate" in data)) {
    data["recurringEndDate"] = "";
  }

  // Always include notes for summary page (if not already present)
  if (!("notes" in data)) {
    data["notes"] = "";
  }
  return data;
}

// Dependency map: when a field changes, which fields should be reset
const MULTI_DAY_CONFIG_DEPENDENTS = [
  "shortEventTitle",
  "briefDescription",
  "anticipatedAttendance",
  "anticipatedAttendanceNumber",
  "setupAccessTime",
  "eventStartTime",
  "eventEndTime",
  "rentalSpaceRequested",
  "kitchenHours",
  "alcoholIncluded",
  "additionalServicesRequested",
  "additionalServicesOtherDetails",
  "notes",
];

const FIELD_DEPENDENCY_MAP: { [key: string]: string[] } = {
  eventDuration: [
    "eventDate",
    "eventStartDate",
    "eventEndDate",
    "selectedDates",
    "multiDayConfiguration",
    "recurringPattern",
    "recurringStartDate",
    "recurringEndDate",
    ...MULTI_DAY_CONFIG_DEPENDENTS,
  ],
  multiDayConfiguration: MULTI_DAY_CONFIG_DEPENDENTS,
  eventStartDate: ["multiDayConfiguration", ...MULTI_DAY_CONFIG_DEPENDENTS],
  eventEndDate: ["multiDayConfiguration", ...MULTI_DAY_CONFIG_DEPENDENTS],
  selectedDates: ["multiDayConfiguration", ...MULTI_DAY_CONFIG_DEPENDENTS],
  rentalSpaceRequested: ["kitchenHours"],
  additionalServicesRequested: ["additionalServicesOtherDetails"],
  anticipatedAttendance: ["anticipatedAttendanceNumber"],
};

// Fields whose change should also clear perDaySchedules
const FIELDS_THAT_CLEAR_PER_DAY = [
  "eventDuration",
  "multiDayConfiguration",
  "eventStartDate",
  "eventEndDate",
  "selectedDates",
];

function getResetValues(
  fieldsToReset: string[],
  initialFormData: FormData,
): Partial<FormData> {
  const resets: Partial<FormData> = {};
  for (const field of fieldsToReset) {
    if (field in initialFormData) {
      resets[field] = initialFormData[field];
    }
  }
  return resets;
}

// Helper function to evaluate a condition recursively
function evaluateCondition(cond: any, formData: FormData): boolean {
  // Handle "includes" for multiselect conditionals
  if (cond.includes !== undefined && cond.field) {
    const value = (formData as any)[cond.field];
    return Array.isArray(value) && value.includes(cond.includes);
  }
  // Handle "equals" for simple equality
  if (cond.equals !== undefined && cond.field) {
    const value = (formData as any)[cond.field];
    return value === cond.equals;
  }
  // Handle "notEquals" for negation
  if (cond.notEquals !== undefined && cond.field) {
    const value = (formData as any)[cond.field];
    return value !== cond.notEquals;
  }
  // Handle "in" for value in array
  if (cond.in !== undefined && cond.field) {
    const value = (formData as any)[cond.field];
    return cond.in.includes(value);
  }
  // Handle "notEmpty" to check if a field has a value
  if (cond.notEmpty !== undefined && cond.field) {
    const value = (formData as any)[cond.field];
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  }
  // Handle "and" for multiple conditions
  if (cond.and) {
    return cond.and.every((subCond: any) =>
      evaluateCondition(subCond, formData),
    );
  }
  // Handle "or" for multiple conditions
  if (cond.or) {
    return cond.or.some((subCond: any) => evaluateCondition(subCond, formData));
  }
  return true;
}

function getVisibleQuestions(formData: FormData) {
  return rentalQuestions.filter((q) => {
    if (!q.conditional) return true;
    return evaluateCondition(q.conditional, formData);
  });
}

function getSectionForQuestion(index: number, visibleQuestions: any[]) {
  // Find the section title for the current question index
  return visibleQuestions[index]?.section || "";
}

// Validation utilities for input sanitization and validation
const ValidationUtils = {
  // Sanitize text input to prevent XSS attacks
  sanitizeText: (input: string): string => {
    if (!input) return "";
    return input.replace(/[<>]/g, ""); // Remove < and > to prevent HTML injection
  },

  // Validate email format
  validateEmail: (email: string): boolean => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254; // RFC 5321
  },

  // Validate phone number (accepts various formats)
  validatePhone: (phone: string): boolean => {
    if (!phone) return false;
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, "");
    // Accept 10 or 11 digits (with or without country code)
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  },

  // Validate URL format
  validateURL: (url: string): boolean => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  },

  // Validate date is not in the past
  validateFutureDate: (dateString: string): boolean => {
    if (!dateString) return false;
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate >= today;
  },

  // Validate time format (HH:MM)
  validateTime: (time: string): boolean => {
    if (!time) return false;
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  },

  // Validate number within range
  validateNumber: (value: number, min?: number, max?: number): boolean => {
    if (value === null || value === undefined || isNaN(value)) return false;
    if (min !== undefined && value < min) return false;
    if (max !== undefined && value > max) return false;
    return true;
  },

  // Get error message for invalid input
  getErrorMessage: (
    fieldType: string,
    fieldLabel: string,
    value: any,
  ): string => {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return `${fieldLabel} is required`;
    }

    switch (fieldType) {
      case "email":
        return "Please enter a valid email address";
      case "tel":
        return "Please enter a valid phone number (10-15 digits)";
      case "url":
        return "Please enter a valid URL (http:// or https://)";
      case "date":
        return "Please select a valid date";
      case "time":
        return "Please enter a valid time (HH:MM)";
      case "number":
        return "Please enter a valid number";
      default:
        return `Please enter a valid ${fieldLabel.toLowerCase()}`;
    }
  },
};

// Maximum input lengths for security
const MAX_LENGTHS = {
  text: 500,
  email: 254,
  tel: 20,
  textarea: 2000,
  url: 2048,
};

export default function RentalQuestionnaire() {
  // Build initialFormData once from rentalQuestions
  const initialFormData = useMemo(
    () => buildInitialFormData(rentalQuestions),
    [],
  );
  const [formData, setFormData] = useState<FormData>({ ...initialFormData });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Error state management for form validation
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  // State for date multi-select calendar
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // State for expanded day cards (tracks which cards are open)
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  // State for per-day customization
  const [perDaySchedules, setPerDaySchedules] = useState<{
    [dateKey: string]: {
      title: string;
      description: string;
      attendance: string;
      attendanceNumber: string;
      setupAccessTime: string;
      startTime: string;
      endTime: string;
      spaces: string[];
      kitchenHours: string;
      alcohol: string;
      additionalServices: string[];
      notes: string;
    };
  }>({});

  // Memoize visible questions based on formData for conditional logic
  const visibleQuestions = useMemo(
    () => getVisibleQuestions(formData),
    [formData],
  );

  // Update a form field and reset all dependent fields
  const updateFormField = useCallback(
    (fieldId: string, value: any) => {
      const fieldsToReset = FIELD_DEPENDENCY_MAP[fieldId] || [];
      const resets = getResetValues(fieldsToReset, initialFormData);

      setFormData((prev) => ({
        ...prev,
        ...resets,
        [fieldId]: value,
      }));

      // Clear per-day schedules when relevant fields change
      if (FIELDS_THAT_CLEAR_PER_DAY.includes(fieldId)) {
        setPerDaySchedules({});
        setExpandedDays(new Set());
      }

      // Clear errors for reset fields
      if (fieldsToReset.length > 0) {
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          for (const f of fieldsToReset) {
            delete newErrors[f];
          }
          return newErrors;
        });
      }
    },
    [initialFormData],
  );

  // Auto-expand first day card when customize-each-day is selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (formData.multiDayConfiguration === "customizeEach") {
      const dates = generateDateArray();
      if (dates.length > 0 && expandedDays.size === 0) {
        const firstDateKey = dates[0].toISOString().split("T")[0];
        setExpandedDays(new Set([firstDateKey]));
      }
    }
  }, [
    formData.multiDayConfiguration,
    formData.eventStartDate,
    formData.eventEndDate,
    formData.selectedDates,
  ]);

  // Validate a single field based on its type
  const validateField = (
    fieldName: string,
    value: any,
    fieldType: string,
  ): string | null => {
    // Find the question definition to get the label and required status
    const question = rentalQuestions.find((q) => q.id === fieldName);
    if (!question) return null;

    const fieldLabel = question.label || fieldName;

    // Skip validation for non-required empty fields
    if (!question.required && (!value || value === "")) {
      return null;
    }

    // Check required fields
    if (question.required) {
      if (
        !value ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return `${fieldLabel} is required`;
      }
    }

    // Type-specific validation
    switch (fieldType) {
      case "email":
        if (value && !ValidationUtils.validateEmail(value)) {
          return ValidationUtils.getErrorMessage(fieldType, fieldLabel, value);
        }
        break;
      case "tel":
        if (value && !ValidationUtils.validatePhone(value)) {
          return ValidationUtils.getErrorMessage(fieldType, fieldLabel, value);
        }
        break;
      case "date":
        if (value && !ValidationUtils.validateFutureDate(value)) {
          return "Please select a date that is today or in the future";
        }
        break;
      case "time":
        if (value && !ValidationUtils.validateTime(value)) {
          return ValidationUtils.getErrorMessage(fieldType, fieldLabel, value);
        }
        break;
      case "number":
        if (value !== "" && !ValidationUtils.validateNumber(Number(value), 0)) {
          return "Please enter a valid positive number";
        }
        break;
      case "url":
        if (value && !ValidationUtils.validateURL(value)) {
          return ValidationUtils.getErrorMessage(fieldType, fieldLabel, value);
        }
        break;
    }

    return null;
  };

  // Form change handler (handles all types, including multiselect, textarea, select)
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, type } = e.target;

    // Clear any existing error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // For checkboxes and text inputs, use type assertions for value/checked
    if (
      type === "checkbox" &&
      (e.target as HTMLInputElement).dataset.multiselect === "true"
    ) {
      // For multiselect, name is the field, value is the option
      const value = (e.target as HTMLInputElement).value;
      const checked = (e.target as HTMLInputElement).checked;
      const arr = Array.isArray(formData[name]) ? [...formData[name]] : [];
      if (checked) {
        if (!arr.includes(value)) arr.push(value);
      } else {
        const idx = arr.indexOf(value);
        if (idx > -1) arr.splice(idx, 1);
      }
      updateFormField(name, arr);
    } else if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      updateFormField(name, checked);
    } else if (type === "number") {
      let value = e.target.value;
      // Prevent negative numbers
      if (value && Number(value) < 0) {
        value = "0";
      }
      updateFormField(name, value === "" ? "" : Number(value));
    } else {
      let value = e.target.value;

      // Apply max length constraints based on input type
      const maxLength =
        type === "textarea"
          ? MAX_LENGTHS.textarea
          : type === "email"
            ? MAX_LENGTHS.email
            : type === "tel"
              ? MAX_LENGTHS.tel
              : MAX_LENGTHS.text;

      if (value.length > maxLength) {
        value = value.substring(0, maxLength);
      }

      // Sanitize text inputs to prevent XSS (except for specific fields that might need special chars)
      if (type === "text" || type === "textarea") {
        value = ValidationUtils.sanitizeText(value);
      }

      updateFormField(name, value);
    }
  };

  // Handle field blur to validate inputs
  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, type } = e.target;
    const value = formData[name];

    // Validate the field and set error if invalid
    const error = validateField(name, value, type);
    if (error) {
      setFieldErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Helper: Calculate number of days for multi-day events
  const calculateEventDays = () => {
    if (formData.eventDuration === "multiDay") {
      const start = new Date(formData.eventStartDate);
      const end = new Date(formData.eventEndDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }
    if (
      formData.eventDuration === "multiDayNonConsecutive" &&
      formData.selectedDates
    ) {
      return formData.selectedDates.length;
    }
    return 1;
  };

  // Helper: Calculate number of occurrences for recurring events
  const calculateRecurringOccurrences = () => {
    if (
      formData.eventDuration === "recurring" &&
      formData.recurringStartDate &&
      formData.recurringEndDate &&
      formData.recurringPattern
    ) {
      const start = new Date(formData.recurringStartDate);
      const end = new Date(formData.recurringEndDate);
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (formData.recurringPattern === "weekly") {
        return Math.floor(diffDays / 7) + 1;
      } else if (formData.recurringPattern === "biweekly") {
        return Math.floor(diffDays / 14) + 1;
      } else if (formData.recurringPattern === "monthly") {
        const months =
          (end.getFullYear() - start.getFullYear()) * 12 +
          (end.getMonth() - start.getMonth());
        return months + 1;
      }
    }
    return 0;
  };

  // Helper: Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    // Add T00:00:00 to prevent timezone issues
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Helper: Format time to AM/PM
  const formatTimeToAMPM = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper: Generate array of dates for multi-day events
  const generateDateArray = () => {
    // Handle consecutive multi-day events
    if (
      formData.eventDuration === "multiDay" &&
      formData.eventStartDate &&
      formData.eventEndDate
    ) {
      const dates: Date[] = [];
      const start = new Date(formData.eventStartDate + "T00:00:00");
      const end = new Date(formData.eventEndDate + "T00:00:00");

      let current = new Date(start);
      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      return dates;
    }

    // Handle non-consecutive multi-day events
    if (
      formData.eventDuration === "multiDayNonConsecutive" &&
      formData.selectedDates
    ) {
      return formData.selectedDates
        .map((dateString: string) => new Date(dateString + "T00:00:00"))
        .sort((a: Date, b: Date) => a.getTime() - b.getTime());
    }

    return [];
  };

  // Helper: Copy schedule from one day to another
  const copyDaySchedule = (fromDate: string, toDate: string) => {
    const fromSchedule = perDaySchedules[fromDate];
    if (fromSchedule) {
      setPerDaySchedules({
        ...perDaySchedules,
        [toDate]: { ...fromSchedule },
      });
    }
  };

  // Helper: Toggle day card expansion
  const toggleDayExpansion = (dateKey: string) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  // Helper: Update schedule for a specific day
  const updateDaySchedule = (dateKey: string, field: string, value: any) => {
    setPerDaySchedules({
      ...perDaySchedules,
      [dateKey]: {
        ...(perDaySchedules[dateKey] || {
          title: "",
          description: "",
          attendance: "",
          attendanceNumber: "",
          setupAccessTime: "",
          startTime: "",
          endTime: "",
          spaces: [],
          kitchenHours: "",
          alcohol: "",
          additionalServices: [],
          notes: "",
        }),
        [field]: value,
      },
    });
  };

  // Helper: Calendar functions for date multi-select
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const toggleDateSelection = (dateString: string) => {
    const selectedDates = formData.selectedDates || [];
    const index = selectedDates.indexOf(dateString);

    if (index > -1) {
      updateFormField(
        "selectedDates",
        selectedDates.filter((d: string) => d !== dateString),
      );
    } else {
      updateFormField("selectedDates", [...selectedDates, dateString].sort());
    }
  };

  const removeDateFromSelection = (dateString: string) => {
    const selectedDates = formData.selectedDates || [];
    updateFormField(
      "selectedDates",
      selectedDates.filter((d: string) => d !== dateString),
    );
  };

  const navigateCalendar = (direction: "prev" | "next") => {
    setCalendarMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === "prev") {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Generate summary data
  const generateSummaryData = () => {
    const summary: { [key: string]: any } = {};

    // Fields to skip in main loop as they get special handling
    const fieldsToSkip = [
      "eventDate",
      "eventStartDate",
      "eventEndDate",
      "selectedDates",
      "multiDayConfiguration",
      "recurringStartDate",
      "recurringEndDate",
      "recurringPattern",
      "anticipatedAttendanceNumber", // Skip the number field, handled with anticipatedAttendance
    ];

    // For customize-each-day, skip these as they're shown per-day
    if (formData.multiDayConfiguration === "customizeEach") {
      fieldsToSkip.push(
        "shortEventTitle",
        "briefDescription",
        "anticipatedAttendance",
        "setupAccessTime",
        "eventStartTime",
        "eventEndTime",
        "rentalSpaceRequested",
        "kitchenHours",
        "alcoholIncluded",
        "additionalServicesRequested",
        "notes",
      );
    }

    visibleQuestions.forEach((q) => {
      // Skip fields that get special handling
      if (fieldsToSkip.includes(q.id)) {
        return;
      }

      const value = formData[q.id];
      if (value !== undefined && value !== null && value !== "") {
        if (q.type === "multiselect" && Array.isArray(value)) {
          if (value.length > 0) {
            summary[q.label] = value.map((v: string) => {
              const option = q.options?.find((opt: any) => opt.value === v);
              return option ? option.label : v;
            });
          }
        } else if (q.type === "checkbox") {
          if (value) {
            summary[q.label] = "Yes";
          }
        } else if (q.id === "anticipatedAttendance") {
          if (value === "openToPublic") {
            summary[q.label] = "Open to the Public";
          } else if (
            value === "specificNumber" &&
            formData["anticipatedAttendanceNumber"]
          ) {
            summary[q.label] =
              `${formData["anticipatedAttendanceNumber"]} attendees`;
          }
        } else if (q.type === "time") {
          // Format time to show AM/PM
          if (value) {
            const [hours, minutes] = value.split(":");
            const hour24 = parseInt(hours);
            const hour12 =
              hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
            const ampm = hour24 >= 12 ? "PM" : "AM";
            summary[q.label] = `${hour12}:${minutes} ${ampm}`;
          }
        } else if (q.type === "select" && q.options) {
          // For select fields, show the label instead of value
          const option = q.options.find((opt: any) => opt.value === value);
          summary[q.label] = option ? option.label : value;
        } else {
          summary[q.label] = value;
        }
      }
    });

    // Add formatted event date summary
    if (formData.eventDuration === "singleDay" && formData.eventDate) {
      summary["Event Date"] = formatDate(formData.eventDate);
    } else if (
      formData.eventDuration === "multiDayNonConsecutive" &&
      formData.selectedDates &&
      formData.selectedDates.length > 0
    ) {
      const days = formData.selectedDates.length;

      if (formData.multiDayConfiguration === "customizeEach") {
        // Show per-day breakdown
        const dates = generateDateArray();
        summary["Event Type"] =
          `Multi-Day Event (${days} selected days, customized per day)`;

        // Show selected dates as array
        summary["Selected Dates"] = formData.selectedDates.map(
          (dateString: string) => {
            const date = new Date(dateString + "T00:00:00");
            return date.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            });
          },
        );

        // Add per-day details
        dates.forEach((date: Date, index: number) => {
          const dateKey = date.toISOString().split("T")[0];
          const daySchedule = perDaySchedules[dateKey];
          if (
            daySchedule &&
            (daySchedule.title ||
              daySchedule.startTime ||
              daySchedule.spaces.length > 0)
          ) {
            const dayLabel = `📅 Day ${index + 1} (${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })})`;

            // Build structured summary object for this day
            const dayData: { [key: string]: any } = {};

            if (daySchedule.title) dayData.title = daySchedule.title;
            if (daySchedule.description)
              dayData.description = daySchedule.description;
            if (daySchedule.attendance === "openToPublic") {
              dayData.attendance = "Open to the Public";
            } else if (
              daySchedule.attendance === "specificNumber" &&
              daySchedule.attendanceNumber
            ) {
              dayData.attendance = `${daySchedule.attendanceNumber} attendees`;
            }

            if (daySchedule.setupAccessTime) {
              dayData.setupAccess = formatTimeToAMPM(
                daySchedule.setupAccessTime,
              );
            }

            if (daySchedule.startTime) {
              dayData.eventStart = formatTimeToAMPM(daySchedule.startTime);
            }
            if (daySchedule.endTime) {
              dayData.eventEnd = formatTimeToAMPM(daySchedule.endTime);
            }

            if (daySchedule.spaces.length > 0) {
              const rentalSpaceQuestion = rentalQuestions.find(
                (rq: any) => rq.id === "rentalSpaceRequested",
              );
              dayData.spaces = daySchedule.spaces.map((s) => {
                const spaceOption = rentalSpaceQuestion?.options?.find(
                  (opt: any) => opt.value === s,
                );
                return spaceOption ? spaceOption.label : s;
              });

              if (
                daySchedule.spaces.includes("commercialKitchen") &&
                daySchedule.kitchenHours
              ) {
                dayData.kitchenHours = daySchedule.kitchenHours;
              }
            }

            if (daySchedule.alcohol) {
              dayData.alcohol = daySchedule.alcohol === "yes";
            }

            if (daySchedule.additionalServices.length > 0) {
              dayData.additionalServices = daySchedule.additionalServices.map(
                (s) => {
                  if (s === "pipeAndDrape") return "Pipe & Drape";
                  if (s === "extraSetupTime") return "Extra Setup Time";
                  return "Other";
                },
              );
            }

            if (daySchedule.notes) dayData.notes = daySchedule.notes;

            summary[dayLabel] = dayData;
          }
        });
      } else {
        const formattedDates = formData.selectedDates.map(
          (dateString: string) => {
            const date = new Date(dateString + "T00:00:00");
            return date.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            });
          },
        );
        summary["Event Type"] =
          `Multi-Day Event (${days} selected day${days !== 1 ? "s" : ""})`;
        summary["Selected Dates"] = formattedDates;
      }
    } else if (
      formData.eventDuration === "multiDay" &&
      formData.eventStartDate &&
      formData.eventEndDate
    ) {
      const days = calculateEventDays();

      if (formData.multiDayConfiguration === "customizeEach") {
        // Show per-day breakdown
        const dates = generateDateArray();
        summary["Event Type"] =
          `Multi-Day Event (${days} days, customized per day)`;
        summary["Event Dates"] =
          `${formatDate(formData.eventStartDate)} - ${formatDate(formData.eventEndDate)}`;

        // Add per-day details
        dates.forEach((date: Date, index: number) => {
          const dateKey = date.toISOString().split("T")[0];
          const daySchedule = perDaySchedules[dateKey];
          if (
            daySchedule &&
            (daySchedule.title ||
              daySchedule.startTime ||
              daySchedule.spaces.length > 0)
          ) {
            const dayLabel = `📅 Day ${index + 1} (${date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })})`;

            // Build structured summary object for this day
            const dayData: { [key: string]: any } = {};

            if (daySchedule.title) dayData.title = daySchedule.title;
            if (daySchedule.description)
              dayData.description = daySchedule.description;
            if (daySchedule.attendance === "openToPublic") {
              dayData.attendance = "Open to the Public";
            } else if (
              daySchedule.attendance === "specificNumber" &&
              daySchedule.attendanceNumber
            ) {
              dayData.attendance = `${daySchedule.attendanceNumber} attendees`;
            }

            if (daySchedule.setupAccessTime) {
              dayData.setupAccess = formatTimeToAMPM(
                daySchedule.setupAccessTime,
              );
            }

            if (daySchedule.startTime) {
              dayData.eventStart = formatTimeToAMPM(daySchedule.startTime);
            }
            if (daySchedule.endTime) {
              dayData.eventEnd = formatTimeToAMPM(daySchedule.endTime);
            }

            if (daySchedule.spaces.length > 0) {
              const rentalSpaceQuestion = rentalQuestions.find(
                (rq: any) => rq.id === "rentalSpaceRequested",
              );
              dayData.spaces = daySchedule.spaces.map((s) => {
                const spaceOption = rentalSpaceQuestion?.options?.find(
                  (opt: any) => opt.value === s,
                );
                return spaceOption ? spaceOption.label : s;
              });

              if (
                daySchedule.spaces.includes("commercialKitchen") &&
                daySchedule.kitchenHours
              ) {
                dayData.kitchenHours = daySchedule.kitchenHours;
              }
            }

            if (daySchedule.alcohol) {
              dayData.alcohol = daySchedule.alcohol === "yes";
            }

            if (daySchedule.additionalServices.length > 0) {
              dayData.additionalServices = daySchedule.additionalServices.map(
                (s) => {
                  if (s === "pipeAndDrape") return "Pipe & Drape";
                  if (s === "extraSetupTime") return "Extra Setup Time";
                  return "Other";
                },
              );
            }

            if (daySchedule.notes) dayData.notes = daySchedule.notes;

            summary[dayLabel] = dayData;
          }
        });
      } else {
        summary["Event Dates"] =
          `${formatDate(formData.eventStartDate)} - ${formatDate(formData.eventEndDate)} (${days} day${days !== 1 ? "s" : ""})`;
      }
    } else if (
      formData.eventDuration === "recurring" &&
      formData.recurringStartDate &&
      formData.recurringEndDate &&
      formData.recurringPattern
    ) {
      const occurrences = calculateRecurringOccurrences();
      const patternLabel =
        formData.recurringPattern === "weekly"
          ? "Weekly"
          : formData.recurringPattern === "biweekly"
            ? "Bi-weekly"
            : "Monthly";
      summary["Event Schedule"] =
        `${patternLabel} from ${formatDate(formData.recurringStartDate)} to ${formatDate(formData.recurringEndDate)} (${occurrences} occurrence${occurrences !== 1 ? "s" : ""})`;
    }

    return summary;
  };

  // Submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all visible required fields before submission
    const errors: { [key: string]: string } = {};
    const visibleQuestionsToValidate = visibleQuestions;

    visibleQuestionsToValidate.forEach((question: any) => {
      const value = formData[question.id];
      const error = validateField(question.id, value, question.type);
      if (error) {
        errors[question.id] = error;
      }
    });

    // Validate day card fields when "customizeEach" is selected
    if (formData.multiDayConfiguration === "customizeEach") {
      const dates = generateDateArray();
      dates.forEach((date: Date, index: number) => {
        const dateKey = date.toISOString().split("T")[0];
        const daySchedule = perDaySchedules[dateKey] || {};
        const dayLabel = `Day ${index + 1}`;

        // Validate required fields for each day
        if (!daySchedule.startTime) {
          errors[`${dateKey}-startTime`] = `${dayLabel}: Event Start Time is required`;
        }
        if (!daySchedule.endTime) {
          errors[`${dateKey}-endTime`] = `${dayLabel}: Event End Time is required`;
        }
        if (!daySchedule.attendance) {
          errors[`${dateKey}-attendance`] = `${dayLabel}: Anticipated Attendance is required`;
        }
        if (daySchedule.attendance === "specificNumber" && !daySchedule.attendanceNumber) {
          errors[`${dateKey}-attendanceNumber`] = `${dayLabel}: Number of Attendees is required`;
        }
        if (!daySchedule.spaces || daySchedule.spaces.length === 0) {
          errors[`${dateKey}-spaces`] = `${dayLabel}: At least one Rental Space must be selected`;
        }
      });
    }

    // If there are validation errors, show them and prevent submission
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmissionError("Please fix the highlighted fields before submitting.");
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];

      // Check if this is a day card field (format: dateKey-fieldName)
      const dayCardMatch = firstErrorField.match(/^(\d{4}-\d{2}-\d{2})-/);
      if (dayCardMatch) {
        const dateKey = dayCardMatch[1];
        // Expand the day card if collapsed
        setExpandedDays((prev) => new Set(Array.from(prev).concat(dateKey)));
        // Wait for expansion animation then scroll
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      } else {
        const errorElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    setSubmissionError(null);
    // All validations passed, proceed with submission
    setSubmitting(true);

    // Use the summary data which has readable labels and formatted values
    const submission = generateSummaryData();

    try {
      await addDoc(collection(db, "rentalRequests"), {
        ...submission,
        submittedAt: serverTimestamp(),
      });
      setSubmitting(false);
      setSubmitted(true);
      // Scroll to the thank-you content inside the container
      setTimeout(() => {
        document
          .getElementById("thank-you-section")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (error) {
      setSubmitting(false);
      setSubmissionError(
        "There was an error submitting your request. Please try again.",
      );
      console.error("Error submitting form:", error);
    }
  };

  const handleRatingSubmit = async () => {
    if (rating === 0) return;
    try {
      await addDoc(collection(db, "formRatings"), {
        rating,
        comment: ratingComment.trim(),
        clientOrganizationName: formData.clientOrganizationName || "",
        submittedAt: serverTimestamp(),
      });
      setRatingSubmitted(true);
    } catch (error) {
      console.error("Error submitting rating:", error);
    }
  };

  // Render a single question field (handles all types, including multiselect and conditional fields)
  function renderQuestionField(q: any) {
    // Special handling for date multi-select calendar
    if (q.id === "selectedDates") {
      const selectedDates = formData.selectedDates || [];
      const { daysInMonth, startingDayOfWeek, year, month } =
        getDaysInMonth(calendarMonth);
      const monthName = calendarMonth.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Generate calendar days
      const calendarDays = [];
      // Add empty cells for days before the first day of the month
      for (let i = 0; i < startingDayOfWeek; i++) {
        calendarDays.push(null);
      }
      // Add actual days
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        date.setHours(0, 0, 0, 0);
        calendarDays.push(date);
      }

      const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      return (
        <FormField>
          <Label htmlFor={q.id}>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}

          <DatePickerContainer>
            <CalendarHeader>
              <CalendarNavButton
                type="button"
                onClick={() => navigateCalendar("prev")}
              >
                ← Previous
              </CalendarNavButton>
              <CalendarMonth>{monthName}</CalendarMonth>
              <CalendarNavButton
                type="button"
                onClick={() => navigateCalendar("next")}
              >
                Next →
              </CalendarNavButton>
            </CalendarHeader>

            <CalendarGrid>
              {dayLabels.map((label) => (
                <DayOfWeekLabel key={label}>{label}</DayOfWeekLabel>
              ))}

              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} />;
                }

                const dateString = date.toISOString().split("T")[0];
                const isSelected = selectedDates.includes(dateString);
                const isPast = date < today;
                const isToday = date.getTime() === today.getTime();

                return (
                  <CalendarDay
                    key={dateString}
                    type="button"
                    isSelected={isSelected}
                    isDisabled={isPast}
                    isToday={isToday}
                    disabled={isPast}
                    onClick={() => toggleDateSelection(dateString)}
                  >
                    {date.getDate()}
                  </CalendarDay>
                );
              })}
            </CalendarGrid>

            <SelectedDatesChips>
              {selectedDates.length > 0 ? (
                selectedDates.sort().map((dateString: string) => {
                  const date = new Date(dateString + "T00:00:00");
                  const formattedDate = date.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  });
                  return (
                    <DateChip key={dateString}>
                      {formattedDate}
                      <button
                        type="button"
                        onClick={() => removeDateFromSelection(dateString)}
                        aria-label={`Remove ${formattedDate}`}
                      >
                        ×
                      </button>
                    </DateChip>
                  );
                })
              ) : (
                <EmptyDatesPlaceholder>
                  Select dates from the calendar above
                </EmptyDatesPlaceholder>
              )}
            </SelectedDatesChips>

            {selectedDates.length > 0 && (
              <FieldNote
                style={{
                  marginTop: "1rem",
                  background: "linear-gradient(135deg, #e8f5e9, #ffffff)",
                  borderLeft: "3px solid #4caf50",
                }}
              >
                ✅ You have selected <strong>{selectedDates.length}</strong> day
                {selectedDates.length !== 1 ? "s" : ""} for your event.
              </FieldNote>
            )}
          </DatePickerContainer>
        </FormField>
      );
    }

    // Special handling for multiDayConfiguration: show day-by-day cards if "customizeEach" is selected
    if (q.id === "multiDayConfiguration") {
      const dates = generateDateArray();
      const showDayCards =
        formData.multiDayConfiguration === "customizeEach" && dates.length > 0;

      return (
        <FormField>
          <Label htmlFor={q.id}>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}

          <DropdownContainer>
            <DropdownHeader
              onClick={() =>
                setOpenDropdownId(openDropdownId === q.id ? null : q.id)
              }
              isOpen={openDropdownId === q.id}
            >
              {formData[q.id]
                ? q.options.find((opt: any) => opt.value === formData[q.id])
                    ?.label
                : "Select configuration"}
              <span
                style={{
                  transform:
                    openDropdownId === q.id ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            </DropdownHeader>
            <DropdownList isOpen={openDropdownId === q.id}>
              {q.options
                .filter((opt: any) => opt.value)
                .map((opt: any) => (
                  <DropdownOption
                    key={opt.value}
                    onClick={() => {
                      updateFormField(q.id, opt.value);
                      setOpenDropdownId(null);
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                      {opt.label}
                    </div>
                    {opt.description && (
                      <div style={{ fontSize: "0.85rem", color: "#666" }}>
                        {opt.description}
                      </div>
                    )}
                  </DropdownOption>
                ))}
            </DropdownList>
          </DropdownContainer>

          {showDayCards && (
            <DayCardsContainer>
              {dates.map((date: Date, index: number) => {
                const dateKey = date.toISOString().split("T")[0];
                const daySchedule = perDaySchedules[dateKey] || {
                  title: "",
                  description: "",
                  attendance: "",
                  attendanceNumber: "",
                  setupAccessTime: "",
                  startTime: "",
                  endTime: "",
                  spaces: [],
                  kitchenHours: "",
                  alcohol: "",
                  additionalServices: [],
                  notes: "",
                };
                const formattedDate = formatDate(dateKey);
                const isExpanded = expandedDays.has(dateKey);

                return (
                  <DayCard key={dateKey}>
                    <DayCardHeader
                      isClickable={true}
                      onClick={() => toggleDayExpansion(dateKey)}
                    >
                      <DayCardTitle>
                        <span>📅</span>
                        Day {index + 1}: {formattedDate}
                        <ExpandIcon isExpanded={isExpanded}>
                          {isExpanded ? "▼" : "▶"}
                        </ExpandIcon>
                      </DayCardTitle>
                      {index > 0 && (
                        <CopyButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyDaySchedule(
                              dates[index - 1].toISOString().split("T")[0],
                              dateKey,
                            );
                          }}
                        >
                          📋 Copy from Day {index}
                        </CopyButton>
                      )}
                    </DayCardHeader>

                    <DayCardContent isExpanded={isExpanded}>
                      {/* Event Title & Description */}
                      <DayCardField style={{ marginBottom: "1rem" }}>
                        <Label htmlFor={`${dateKey}-title`}>
                          Event Title for This Day
                        </Label>
                        <Input
                          id={`${dateKey}-title`}
                          type="text"
                          value={daySchedule.title}
                          onChange={(e) =>
                            updateDaySchedule(dateKey, "title", e.target.value)
                          }
                          placeholder="e.g., Day 1: Setup & Registration"
                        />
                      </DayCardField>

                      <DayCardField style={{ marginBottom: "1rem" }}>
                        <Label htmlFor={`${dateKey}-description`}>
                          Brief Description
                        </Label>
                        <Textarea
                          id={`${dateKey}-description`}
                          value={daySchedule.description}
                          onChange={(e) =>
                            updateDaySchedule(
                              dateKey,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Describe what happens on this day"
                          rows={3}
                        />
                      </DayCardField>

                      <DayCardField style={{ marginBottom: "1rem" }}>
                        <Label>Anticipated Attendance<span style={{ color: "#dc3545" }}> *</span></Label>
                        <CheckboxGroup>
                          {/* Radio: Open to Public */}
                          <CheckboxLabel
                            htmlFor={`${dateKey}-attendance-public`}
                            style={{ width: "100%" }}
                          >
                            <Input
                              id={`${dateKey}-attendance-public`}
                              type="radio"
                              name={`${dateKey}-attendanceType`}
                              checked={
                                daySchedule.attendance === "openToPublic"
                              }
                              onChange={() => {
                                setPerDaySchedules({
                                  ...perDaySchedules,
                                  [dateKey]: {
                                    ...daySchedule,
                                    attendance: "openToPublic",
                                    attendanceNumber: "",
                                  },
                                });
                              }}
                            />
                            Open to the Public
                          </CheckboxLabel>

                          {/* Radio: Specific Number */}
                          <CheckboxLabel
                            htmlFor={`${dateKey}-attendance-number`}
                            style={{ width: "100%" }}
                          >
                            <Input
                              id={`${dateKey}-attendance-number`}
                              type="radio"
                              name={`${dateKey}-attendanceType`}
                              checked={
                                daySchedule.attendance === "specificNumber"
                              }
                              onChange={() => {
                                updateDaySchedule(
                                  dateKey,
                                  "attendance",
                                  "specificNumber",
                                );
                              }}
                            />
                            Expected Number of Attendees
                          </CheckboxLabel>
                        </CheckboxGroup>

                        {/* Number Input - only show if "Specific Number" is selected */}
                        {daySchedule.attendance === "specificNumber" && (
                          <div
                            style={{ marginTop: "1rem", marginLeft: "2.5rem" }}
                          >
                            <Input
                              id={`${dateKey}-attendanceNumber`}
                              type="number"
                              value={daySchedule.attendanceNumber}
                              onChange={(e) =>
                                updateDaySchedule(
                                  dateKey,
                                  "attendanceNumber",
                                  e.target.value,
                                )
                              }
                              placeholder="Enter number of attendees"
                              min="1"
                            />
                          </div>
                        )}
                      </DayCardField>

                      {/* Setup Access Time */}
                      <DayCardField style={{ marginBottom: "1rem" }}>
                        <Label htmlFor={`${dateKey}-setupAccessTime`}>
                          Setup Access Time
                        </Label>
                        <FieldNote>
                          When do you want to access the space for setting up?
                        </FieldNote>
                        <Input
                          id={`${dateKey}-setupAccessTime`}
                          type="time"
                          value={daySchedule.setupAccessTime}
                          onChange={(e) =>
                            updateDaySchedule(
                              dateKey,
                              "setupAccessTime",
                              e.target.value,
                            )
                          }
                        />
                      </DayCardField>

                      {/* Event Times */}
                      <DayCardGrid>
                        <DayCardField>
                          <Label htmlFor={`${dateKey}-startTime`}>
                            Event Start Time<span style={{ color: "#dc3545" }}> *</span>
                          </Label>
                          <Input
                            id={`${dateKey}-startTime`}
                            type="time"
                            value={daySchedule.startTime}
                            onChange={(e) =>
                              updateDaySchedule(
                                dateKey,
                                "startTime",
                                e.target.value,
                              )
                            }
                          />
                        </DayCardField>

                        <DayCardField>
                          <Label htmlFor={`${dateKey}-endTime`}>
                            Event End Time<span style={{ color: "#dc3545" }}> *</span>
                          </Label>
                          <Input
                            id={`${dateKey}-endTime`}
                            type="time"
                            value={daySchedule.endTime}
                            onChange={(e) =>
                              updateDaySchedule(
                                dateKey,
                                "endTime",
                                e.target.value,
                              )
                            }
                          />
                        </DayCardField>
                      </DayCardGrid>

                      {/* Rental Spaces */}
                      <DayCardField style={{ marginTop: "1rem" }}>
                        <Label>Rental Spaces for This Day<span style={{ color: "#dc3545" }}> *</span></Label>
                        <FieldNote>
                          If you are selecting the Full Hall, this will
                          automatically include the Market Café as is, meaning
                          tables and chairs will remain in place. If you wish to
                          use the Market Café space without the tables and
                          chairs (i.e., cleared), please make sure to select
                          Market Café as a separate option.
                        </FieldNote>

                        {/* Market Map Image for Reference */}
                        <div
                          style={{
                            textAlign: "center",
                            margin: "1.5rem 0",
                          }}
                        >
                          <MarketMapImage
                            src={marketMap}
                            alt="Market Layout Map - Reference for rental spaces"
                            title="Click to view full size"
                            onClick={() => setIsImageModalOpen(true)}
                          />
                          <p
                            style={{
                              fontSize: "0.85rem",
                              color: "#666",
                              marginTop: "0.5rem",
                              fontStyle: "italic",
                            }}
                          >
                            Market layout for reference - click image to view
                            full size
                          </p>
                        </div>

                        <CheckboxGroup>
                          {(
                            rentalQuestions.find(
                              (rq: any) => rq.id === "rentalSpaceRequested",
                            )?.options || []
                          )
                            .filter((opt: any) => opt.value)
                            .map((opt: any) => (
                              <CheckboxLabel
                                key={opt.value}
                                htmlFor={`${dateKey}-${opt.value}`}
                              >
                                <Input
                                  id={`${dateKey}-${opt.value}`}
                                  type="checkbox"
                                  checked={daySchedule.spaces.includes(
                                    opt.value,
                                  )}
                                  onChange={(e) => {
                                    const newSpaces = e.target.checked
                                      ? [...daySchedule.spaces, opt.value]
                                      : daySchedule.spaces.filter(
                                          (s) => s !== opt.value,
                                        );
                                    updateDaySchedule(
                                      dateKey,
                                      "spaces",
                                      newSpaces,
                                    );
                                  }}
                                />
                                {opt.label}
                              </CheckboxLabel>
                            ))}
                        </CheckboxGroup>
                      </DayCardField>

                      {/* Kitchen Hours - conditional on kitchen selection */}
                      {daySchedule.spaces.includes("commercialKitchen") && (
                        <DayCardField style={{ marginTop: "1rem" }}>
                          <Label htmlFor={`${dateKey}-kitchenHours`}>
                            How many hours will you use the kitchen?
                          </Label>
                          <Input
                            id={`${dateKey}-kitchenHours`}
                            type="number"
                            min="1"
                            value={daySchedule.kitchenHours}
                            onChange={(e) =>
                              updateDaySchedule(
                                dateKey,
                                "kitchenHours",
                                e.target.value,
                              )
                            }
                            placeholder="Enter number of hours"
                          />
                        </DayCardField>
                      )}

                      {/* Alcohol */}
                      <DayCardField style={{ marginTop: "1rem" }}>
                        <Label htmlFor={`${dateKey}-alcohol`}>
                          Will this day include alcohol?
                        </Label>
                        <DropdownContainer>
                          <DropdownHeader
                            onClick={() =>
                              setOpenDropdownId(
                                openDropdownId === `${dateKey}-alcohol`
                                  ? null
                                  : `${dateKey}-alcohol`,
                              )
                            }
                            isOpen={openDropdownId === `${dateKey}-alcohol`}
                          >
                            {daySchedule.alcohol === "yes"
                              ? "Yes"
                              : daySchedule.alcohol === "no"
                                ? "No"
                                : "Select"}
                            <span
                              style={{
                                transform:
                                  openDropdownId === `${dateKey}-alcohol`
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                              }}
                            >
                              ▼
                            </span>
                          </DropdownHeader>
                          <DropdownList
                            isOpen={openDropdownId === `${dateKey}-alcohol`}
                          >
                            <DropdownOption
                              onClick={() => {
                                updateDaySchedule(dateKey, "alcohol", "yes");
                                setOpenDropdownId(null);
                              }}
                            >
                              Yes
                            </DropdownOption>
                            <DropdownOption
                              onClick={() => {
                                updateDaySchedule(dateKey, "alcohol", "no");
                                setOpenDropdownId(null);
                              }}
                            >
                              No
                            </DropdownOption>
                          </DropdownList>
                        </DropdownContainer>
                      </DayCardField>

                      {/* Additional Services */}
                      <DayCardField style={{ marginTop: "1rem" }}>
                        <Label>Additional Services Requested</Label>
                        <CheckboxGroup>
                          <CheckboxLabel htmlFor={`${dateKey}-pipeAndDrape`}>
                            <Input
                              id={`${dateKey}-pipeAndDrape`}
                              type="checkbox"
                              checked={daySchedule.additionalServices.includes(
                                "pipeAndDrape",
                              )}
                              onChange={(e) => {
                                const newServices = e.target.checked
                                  ? [
                                      ...daySchedule.additionalServices,
                                      "pipeAndDrape",
                                    ]
                                  : daySchedule.additionalServices.filter(
                                      (s) => s !== "pipeAndDrape",
                                    );
                                updateDaySchedule(
                                  dateKey,
                                  "additionalServices",
                                  newServices,
                                );
                              }}
                            />
                            Pipe & Drape
                          </CheckboxLabel>
                          <CheckboxLabel htmlFor={`${dateKey}-extraSetupTime`}>
                            <Input
                              id={`${dateKey}-extraSetupTime`}
                              type="checkbox"
                              checked={daySchedule.additionalServices.includes(
                                "extraSetupTime",
                              )}
                              onChange={(e) => {
                                const newServices = e.target.checked
                                  ? [
                                      ...daySchedule.additionalServices,
                                      "extraSetupTime",
                                    ]
                                  : daySchedule.additionalServices.filter(
                                      (s) => s !== "extraSetupTime",
                                    );
                                updateDaySchedule(
                                  dateKey,
                                  "additionalServices",
                                  newServices,
                                );
                              }}
                            />
                            Extra Setup Time
                          </CheckboxLabel>
                          <CheckboxLabel htmlFor={`${dateKey}-other`}>
                            <Input
                              id={`${dateKey}-other`}
                              type="checkbox"
                              checked={daySchedule.additionalServices.includes(
                                "other",
                              )}
                              onChange={(e) => {
                                const newServices = e.target.checked
                                  ? [...daySchedule.additionalServices, "other"]
                                  : daySchedule.additionalServices.filter(
                                      (s) => s !== "other",
                                    );
                                updateDaySchedule(
                                  dateKey,
                                  "additionalServices",
                                  newServices,
                                );
                              }}
                            />
                            Other
                          </CheckboxLabel>
                        </CheckboxGroup>
                      </DayCardField>

                      {/* Notes */}
                      <DayCardField style={{ marginTop: "1rem" }}>
                        <Label htmlFor={`${dateKey}-notes`}>
                          Notes for This Day (Optional)
                        </Label>
                        <Textarea
                          id={`${dateKey}-notes`}
                          value={daySchedule.notes}
                          onChange={(e) =>
                            updateDaySchedule(dateKey, "notes", e.target.value)
                          }
                          placeholder="Any special requirements or notes for this specific day"
                          rows={2}
                        />
                      </DayCardField>
                    </DayCardContent>
                  </DayCard>
                );
              })}
            </DayCardsContainer>
          )}

          {formData.multiDayConfiguration === "sameForAll" &&
            dates.length > 0 && (
              <FieldNote
                style={{
                  background: "linear-gradient(135deg, #e8f5e9, #ffffff)",
                  borderLeft: "3px solid #4caf50",
                  marginTop: "1rem",
                }}
              >
                ✅ Great! You'll fill out the time and space details below, and
                they'll apply to all {dates.length} days of your event.
              </FieldNote>
            )}
        </FormField>
      );
    }

    // Special handling for eventEndDate: add validation and helpful info
    if (q.id === "eventEndDate") {
      const days = calculateEventDays();
      const hasValidDates = formData.eventStartDate && formData.eventEndDate;
      const startDate = formData.eventStartDate
        ? new Date(formData.eventStartDate)
        : null;
      const endDate = formData.eventEndDate
        ? new Date(formData.eventEndDate)
        : null;
      const isValidRange = startDate && endDate && endDate >= startDate;

      return (
        <FormField>
          <Label htmlFor={q.id}>
            {q.label}
            {q.required && <span style={{ color: "#dc3545" }}> *</span>}
          </Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <Input
            id={q.id}
            type="date"
            name={q.id}
            value={formData[q.id] ?? ""}
            min={formData.eventStartDate || undefined}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={q.placeholder || ""}
            required={q.required}
            style={
              fieldErrors[q.id]
                ? {
                    borderColor: "#dc3545",
                  }
                : {}
            }
          />
          {fieldErrors[q.id] && (
            <ErrorMessage>{fieldErrors[q.id]}</ErrorMessage>
          )}
          {hasValidDates && isValidRange && days > 0 && (
            <FieldNote
              style={{
                background: "linear-gradient(135deg, #e8f5e9, #ffffff)",
                borderLeft: "3px solid #4caf50",
              }}
            >
              📅 Your event spans{" "}
              <strong>
                {days} consecutive day{days !== 1 ? "s" : ""}
              </strong>
              . The rental cost will be calculated for all {days} day
              {days !== 1 ? "s" : ""}.
            </FieldNote>
          )}
          {hasValidDates && !isValidRange && (
            <FieldNote
              style={{
                background: "linear-gradient(135deg, #ffebee, #ffffff)",
                borderLeft: "3px solid #f44336",
              }}
            >
              ⚠️ End date must be on or after the start date
            </FieldNote>
          )}
        </FormField>
      );
    }

    // Special handling for recurringEndDate: show occurrence count
    if (q.id === "recurringEndDate") {
      const occurrences = calculateRecurringOccurrences();
      const hasValidDates =
        formData.recurringStartDate &&
        formData.recurringEndDate &&
        formData.recurringPattern;
      const startDate = formData.recurringStartDate
        ? new Date(formData.recurringStartDate)
        : null;
      const endDate = formData.recurringEndDate
        ? new Date(formData.recurringEndDate)
        : null;
      const isValidRange = startDate && endDate && endDate >= startDate;

      return (
        <FormField>
          <Label htmlFor={q.id}>
            {q.label}
            {q.required && <span style={{ color: "#dc3545" }}> *</span>}
          </Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <Input
            id={q.id}
            type="date"
            name={q.id}
            value={formData[q.id] ?? ""}
            min={formData.recurringStartDate || undefined}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={q.placeholder || ""}
            required={q.required}
            style={
              fieldErrors[q.id]
                ? {
                    borderColor: "#dc3545",
                  }
                : {}
            }
          />
          {fieldErrors[q.id] && (
            <ErrorMessage>{fieldErrors[q.id]}</ErrorMessage>
          )}
          {hasValidDates && isValidRange && occurrences > 0 && (
            <FieldNote
              style={{
                background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
                borderLeft: "3px solid #2196f3",
              }}
            >
              🔄 This will create{" "}
              <strong>
                {occurrences}{" "}
                {formData.recurringPattern === "weekly"
                  ? "weekly"
                  : formData.recurringPattern === "biweekly"
                    ? "bi-weekly"
                    : "monthly"}{" "}
                occurrence{occurrences !== 1 ? "s" : ""}
              </strong>{" "}
              from {formatDate(formData.recurringStartDate)} to{" "}
              {formatDate(formData.recurringEndDate)}.
            </FieldNote>
          )}
          {hasValidDates && !isValidRange && (
            <FieldNote
              style={{
                background: "linear-gradient(135deg, #ffebee, #ffffff)",
                borderLeft: "3px solid #f44336",
              }}
            >
              ⚠️ Last event date must be on or after the first event date
            </FieldNote>
          )}
        </FormField>
      );
    }

    // Special handling for anticipatedAttendance: render as radio buttons with conditional input
    if (q.id === "anticipatedAttendance") {
      const isOpenToPublic =
        formData["anticipatedAttendance"] === "openToPublic";
      const isSpecificNumber =
        formData["anticipatedAttendance"] === "specificNumber";
      const attendanceNumber = formData["anticipatedAttendanceNumber"] || "";

      return (
        <FormField>
          <Label>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}

          <CheckboxGroup>
            {/* Radio: Open to Public */}
            <CheckboxLabel
              htmlFor="attendance-public"
              style={{ width: "100%" }}
            >
              <Input
                id="attendance-public"
                type="radio"
                name="anticipatedAttendanceType"
                checked={isOpenToPublic}
                onChange={() => {
                  updateFormField("anticipatedAttendance", "openToPublic");
                }}
              />
              Open to the Public
            </CheckboxLabel>

            {/* Radio: Specific Number */}
            <CheckboxLabel
              htmlFor="attendance-number"
              style={{ width: "100%" }}
            >
              <Input
                id="attendance-number"
                type="radio"
                name="anticipatedAttendanceType"
                checked={isSpecificNumber}
                onChange={() => {
                  updateFormField("anticipatedAttendance", "specificNumber");
                }}
              />
              Expected Number of Attendees
            </CheckboxLabel>
          </CheckboxGroup>

          {/* Number Input - only show if "Specific Number" is selected */}
          {isSpecificNumber && (
            <div style={{ marginTop: "1rem", marginLeft: "2.5rem" }}>
              <Input
                id="anticipatedAttendanceNumber"
                type="number"
                name="anticipatedAttendanceNumber"
                value={attendanceNumber}
                onChange={(e) => {
                  updateFormField(
                    "anticipatedAttendanceNumber",
                    e.target.value,
                  );
                }}
                placeholder="Enter number of attendees"
                min="1"
              />
            </div>
          )}
        </FormField>
      );
    }

    // Special handling for rentalSpaceRequested: render as a multiselect checkbox group
    if (q.id === "rentalSpaceRequested") {
      // We'll render as a multiselect checkbox group
      // Ensure formData["rentalSpaceRequested"] is an array
      const values = Array.isArray(formData["rentalSpaceRequested"])
        ? formData["rentalSpaceRequested"]
        : [];
      return (
        <FormField>
          <Label>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}

          {/* Market Map Image for Reference */}
          <div style={{ textAlign: "center", margin: "1.5rem 0" }}>
            <MarketMapImage
              src={marketMap}
              alt="Market Layout Map - Reference for rental spaces"
              title="Click to view full size"
              onClick={() => setIsImageModalOpen(true)}
            />
            <p
              style={{
                fontSize: "0.85rem",
                color: "#666",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Market layout for reference - click image to view full size
            </p>
          </div>

          <CheckboxGroup>
            {q.options.map((opt: any) => (
              <CheckboxLabel
                key={opt.value}
                htmlFor={`rentalSpaceRequested_${opt.value}`}
              >
                <Input
                  id={`rentalSpaceRequested_${opt.value}`}
                  type="checkbox"
                  name="rentalSpaceRequested"
                  value={opt.value}
                  data-multiselect="true"
                  checked={values.includes(opt.value)}
                  onChange={handleChange}
                />
                {opt.label}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>

          {/* If "Other" is selected, show a conditional text input */}
          {q.options.some((opt: any) => opt.value === "Other") &&
            values.includes("Other") && (
              <FormField style={{ marginTop: "1.2rem" }}>
                <Label htmlFor={`rentalSpaceRequestedOtherDetails`}>
                  Please specify other:
                </Label>
                <Input
                  id={`rentalSpaceRequestedOtherDetails`}
                  type="text"
                  name={`rentalSpaceRequestedOtherDetails`}
                  value={formData[`rentalSpaceRequestedOtherDetails`] || ""}
                  onChange={handleChange}
                  placeholder="Enter details"
                />
              </FormField>
            )}
        </FormField>
      );
    }
    // Multiselect (checkbox group)
    if (q.type === "multiselect") {
      const values = formData[q.id] || [];
      return (
        <FormField>
          <Label>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <CheckboxGroup>
            {q.options.map((opt: any) => (
              <CheckboxLabel key={opt.value} htmlFor={`${q.id}_${opt.value}`}>
                <Input
                  id={`${q.id}_${opt.value}`}
                  type="checkbox"
                  name={q.id}
                  value={opt.value}
                  data-multiselect="true"
                  checked={values.includes(opt.value)}
                  onChange={handleChange}
                />
                {opt.label}
              </CheckboxLabel>
            ))}
          </CheckboxGroup>
          {/* If "Other" is selected, show a conditional text input */}
          {q.options.some((opt: any) => opt.value === "Other") &&
            values.includes("Other") && (
              <FormField style={{ marginTop: "1.2rem" }}>
                <Label htmlFor={`${q.id}OtherDetails`}>
                  Please specify other:
                </Label>
                <Input
                  id={`${q.id}OtherDetails`}
                  type="text"
                  name={`${q.id}OtherDetails`}
                  value={formData[`${q.id}OtherDetails`] || ""}
                  onChange={handleChange}
                  placeholder="Enter details"
                />
              </FormField>
            )}
        </FormField>
      );
    }
    // Checkbox is special: show as a single checkbox in the center
    if (q.type === "checkbox") {
      return (
        <CheckboxGroup>
          <CheckboxLabel htmlFor={q.id}>
            <Label
              htmlFor={q.id}
              style={{
                marginBottom: 0,
                marginRight: "0.5rem",
                fontWeight: 600,
                fontSize: "1.15rem",
                color: "#2a3a8c",
                display: "none",
              }}
            >
              {q.label}
            </Label>
            {q.note && <FieldNote>{q.note}</FieldNote>}
            <Input
              id={q.id}
              type="checkbox"
              name={q.id}
              checked={!!formData[q.id]}
              onChange={handleChange}
            />
            {q.label}
          </CheckboxLabel>
        </CheckboxGroup>
      );
    }
    // Textarea
    if (q.type === "textarea") {
      return (
        <FormField>
          <Label htmlFor={q.id}>
            {q.label}
            {q.required && <span style={{ color: "#dc3545" }}> *</span>}
          </Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <Textarea
            id={q.id}
            name={q.id}
            value={formData[q.id]}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={q.placeholder || ""}
            maxLength={MAX_LENGTHS.textarea}
            style={
              fieldErrors[q.id]
                ? {
                    borderColor: "#dc3545",
                  }
                : {}
            }
          />
          {fieldErrors[q.id] && (
            <ErrorMessage>{fieldErrors[q.id]}</ErrorMessage>
          )}
        </FormField>
      );
    }
    // Select dropdown
    if (q.type === "select") {
      const isOpen = openDropdownId === q.id;
      const value = formData[q.id];
      const handleOptionClick = (val: string) => {
        updateFormField(q.id, val);
        setOpenDropdownId(null);
      };
      return (
        <FormField>
          <Label htmlFor={q.id}>{q.label}</Label>
          {q.note && <FieldNote>{q.note}</FieldNote>}
          <DropdownContainer>
            <DropdownHeader
              onClick={() => setOpenDropdownId(isOpen ? null : q.id)}
              isOpen={isOpen}
            >
              {value
                ? q.options.find((opt: any) => opt.value === value)?.label
                : "Select an option"}
              <span
                style={{
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                ▼
              </span>
            </DropdownHeader>
            <DropdownList isOpen={isOpen}>
              {q.options.map((opt: any) => (
                <DropdownOption
                  key={opt.value}
                  onClick={() => handleOptionClick(opt.value)}
                >
                  {opt.label}
                </DropdownOption>
              ))}
            </DropdownList>
          </DropdownContainer>
        </FormField>
      );
    }
    // Conditional fields (e.g., kitchenHours for kitchen rental)
    // (handled inline in rentalSpaceRequested multiselect above)
    // Conditional text input for "Other" in multiselect handled above
    // Number, text, email, tel, date, time
    return (
      <FormField>
        <Label htmlFor={q.id}>
          {q.label}
          {q.required && <span style={{ color: "#dc3545" }}> *</span>}
        </Label>
        {q.note && <FieldNote>{q.note}</FieldNote>}
        <Input
          id={q.id}
          type={q.type}
          name={q.id}
          value={formData[q.id] ?? ""}
          min={q.min}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={q.placeholder || ""}
          required={q.required}
          maxLength={
            q.type === "email"
              ? MAX_LENGTHS.email
              : q.type === "tel"
                ? MAX_LENGTHS.tel
                : MAX_LENGTHS.text
          }
          style={
            fieldErrors[q.id]
              ? {
                  borderColor: "#dc3545",
                }
              : {}
          }
        />
        {fieldErrors[q.id] && <ErrorMessage>{fieldErrors[q.id]}</ErrorMessage>}
      </FormField>
    );
  }

  // All questions are always visible (no progressive reveal)
  type VisibleQuestion = {
    question: any;
    index: number;
    section: string;
    showSectionHeader: boolean;
  };
  const verticalVisibleQuestions: VisibleQuestion[] = visibleQuestions.map(
    (q, i) => {
      const section = getSectionForQuestion(i, visibleQuestions);
      // show section header if section is non-empty and different from previous
      const showSectionHeader =
        i === 0 || section !== getSectionForQuestion(i - 1, visibleQuestions);
      return {
        question: q,
        index: i,
        section,
        showSectionHeader,
      };
    },
  );

  return (
    <div>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroText>
            <SectionTitle
              parts={[{ text: "🏢 SJFM Rental Questionnaire", color: "white" }]}
              align="left"
            />
            <p style={{ fontSize: "1.25rem", lineHeight: "1.6" }}>
              Thank you for your interest in renting space at the St. John's
              Farmers' Market. Please provide your event details, and we'll
              review your request, calculate the cost, and send a contract for
              your approval.
            </p>
          </HeroText>
          <HeroAnimation>
            <DotLottieReact
              src={rentalForm}
              loop
              autoplay
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </HeroAnimation>
        </HeroContent>
      </HeroSection>
      {/* Main Container */}
      <Container>
        {submitted ? (
          <ThankYouContainer id="thank-you-section">
            <ThankYouIcon>
              <svg viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </ThankYouIcon>
            <ThankYouTitle>Thank You!</ThankYouTitle>
            <ThankYouMessage>
              Your rental request has been submitted successfully. We'll review
              your application and get back to you soon.
            </ThankYouMessage>

            {!ratingSubmitted ? (
              <RatingSection>
                <RatingLabel>How was your experience with this form?</RatingLabel>
                <StarsContainer>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      type="button"
                      filled={star <= (ratingHover || rating)}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setRatingHover(star)}
                      onMouseLeave={() => setRatingHover(0)}
                      aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                    >
                      <svg viewBox="0 0 24 24">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </Star>
                  ))}
                </StarsContainer>
                <RatingTextarea
                  placeholder="Any feedback or suggestions? (optional)"
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  maxLength={1000}
                />
                <RatingSubmitButton
                  onClick={handleRatingSubmit}
                  disabled={rating === 0}
                >
                  Submit Feedback
                </RatingSubmitButton>
              </RatingSection>
            ) : (
              <RatingThankYou>Thanks for your feedback!</RatingThankYou>
            )}
          </ThankYouContainer>
        ) : (
          <>
            {submissionError && (
              <ErrorBanner>{submissionError}</ErrorBanner>
            )}
            <form
              id="rental-form"
              onSubmit={handleSubmit}
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              <FormLayout>
                <StepContentScroll>
                  {/* Render all questions vertically, all always visible */}
                  {verticalVisibleQuestions.map(
                    ({ question: q, section, showSectionHeader }) => (
                      <React.Fragment key={q.id}>
                        {showSectionHeader && (
                          <AnimatedSectionTitle
                            animate={true}
                            style={{ marginBottom: "2.5rem" }}
                          >
                            {section}
                          </AnimatedSectionTitle>
                        )}
                        {renderQuestionField(q)}
                      </React.Fragment>
                    ),
                  )}
                </StepContentScroll>
              </FormLayout>
            </form>

            {/* Submit Button */}
            <SummaryActions>
              <Button type="submit" disabled={submitting} form="rental-form">
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            </SummaryActions>
          </>
        )}
      </Container>

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        onClick={() => setIsImageModalOpen(false)}
      >
        <CloseButton
          onClick={(e) => {
            e.stopPropagation();
            setIsImageModalOpen(false);
          }}
        >
          ×
        </CloseButton>
        <ModalImage
          src={marketMap}
          alt="Market Layout Map - Full Size"
          onClick={(e) => e.stopPropagation()}
        />
      </ImageModal>
    </div>
  );
}
